import { useState, useEffect, useMemo, useRef } from 'react';
import { UserProfile, Expert, Conversation, ChatMessage } from '../../types';
import { Loader2, Search, Briefcase, Check, CheckCheck } from 'lucide-react';

// --- Column 1: User List ---
const CmsUserList: React.FC<{ users: UserProfile[], selectedUserId: number | null, onSelect: (user: UserProfile) => void }> = ({ users, selectedUserId, onSelect }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const filteredUsers = useMemo(() => {
        if (!searchTerm) return users;
        return users.filter(u => u.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [users, searchTerm]);

    return (
        <div className="bg-mystic-box h-full flex flex-col">
            <div className="p-3 border-b border-gray-800">
                <div className="relative">
                    <input type="text" placeholder="Search users..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                        className="w-full bg-mystic-inset border border-gray-700 rounded-full pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-mystic-accent" />
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                </div>
            </div>
            <div className="flex-1 overflow-y-auto">
                {filteredUsers.length > 0 ? (
                    <ul>
                        {filteredUsers.map(user => (
                            <li key={user.id}>
                                <button onClick={() => onSelect(user)} className={`w-full text-left flex items-center gap-3 p-3 transition-colors ${selectedUserId === user.id ? 'bg-mystic-accent/10' : 'hover:bg-white/5'}`}>
                                    <img src={user.avatar || `https://i.pravatar.cc/150?u=${user.id}`} alt={user.name} className="w-10 h-10 rounded-full object-cover" />
                                    <div className="flex-1 overflow-hidden">
                                        <h3 className="font-bold text-white truncate flex items-center gap-2">
                                            <span className="truncate">{user.name}</span>
                                            {Number(user.unread_count) > 0 && (
                                                <span className="inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] min-w-[18px] h-[18px] px-1">
                                                    {Math.min(Number(user.unread_count), 99)}
                                                </span>
                                            )}
                                        </h3>
                                        <p className="text-xs text-gray-500 truncate">
                                            {user.last_message || 'Brak wiadomości'}
                                        </p>
                                    </div>
                                </button>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className="p-8 text-center text-sm text-gray-500">No users found.</div>
                )}
            </div>
        </div>
    );
};

// --- Column 2: Expert Conversation List ---
const CmsExpertList: React.FC<{ conversations: Conversation[], selectedExpertId: number | null, onSelect: (expert: Expert) => void }> = ({ conversations, selectedExpertId, onSelect }) => {
    return (
        <div className="bg-mystic-bg h-full flex flex-col">
            <div className="p-4 border-b border-gray-800">
                <h2 className="font-bold text-white flex items-center gap-2"><Briefcase className="w-4 h-4"/> Conversations</h2>
            </div>
            <div className="flex-1 overflow-y-auto">
                {conversations.length > 0 ? (
                    <ul>
                        {conversations.map(convo => (
                            <li key={convo.user_id}>
                                <button
                                    onClick={() => onSelect(convo as any as Expert)}
                                    className={`w-full text-left flex items-center gap-3 p-3 transition-colors ${selectedExpertId === convo.rowid ? 'bg-mystic-accent/20' : 'hover:bg-white/10'}`}
                                >
                                    <img src={convo.avatar_img || `https://i.pravatar.cc/150?u=${convo.rowid}`} alt={convo.name} className="w-10 h-10 rounded-full object-cover" />
                                    <div className="flex-1 overflow-hidden">
                                        <h3 className="font-bold text-white truncate flex items-center gap-2">
                                            <span className="truncate">{convo.name}</span>
                                            {Number(convo.unread_count) > 0 && (
                                                <span className="inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] min-w-[18px] h-[18px] px-1">
                                                    {Math.min(Number(convo.unread_count), 99)}
                                                </span>
                                            )}
                                        </h3>
                                        <p className="text-xs text-gray-400 truncate">{convo.last_message}</p>
                                    </div>
                                </button>
                            </li>
                        ))}
                    </ul>
                ) : (
                     <div className="p-8 text-center text-sm text-gray-500">No conversations for this user.</div>
                )}
            </div>
        </div>
    );
};

// --- Column 3: Chat Window ---
const CmsChatWindow: React.FC<{ 
    user: UserProfile, 
    expert: Expert, 
    messages: ChatMessage[],
    onSendMessage: (text: string) => void 
}> = ({ user, expert, messages, onSendMessage }) => {
    const messagesEndRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const [text, setText] = useState('');
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (text.trim()) {
            onSendMessage(text);
            setText('');
        }
    };

    return (
        <div className="bg-mystic-btn h-full flex flex-col">
            <div className="p-4 border-b border-gray-800 bg-mystic-bg">
                <h2 className="font-bold text-white">Rozmowa: {user.name} ↔ {expert.name}</h2>
                <p className="text-xs text-gray-500">Admin view - sending as expert</p>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map(msg => {
                    const isExpertMessage = msg.sender_id === expert.user_id;
                    const isRead = !!msg.date_read;
                    const readDate = msg.date_read ? new Date(msg.date_read) : null;
                    return (
                        <div key={msg.id} className={`flex items-start gap-3 ${isExpertMessage ? 'flex-row-reverse' : ''}`}>
                             <img src={isExpertMessage ? expert.avatar_img || '' : user.avatar || ''} className="w-8 h-8 rounded-full object-cover" />
                             <div className={`max-w-md p-3 rounded-lg ${isExpertMessage ? 'bg-mystic-accent text-black' : 'bg-gray-700 text-white'}`}>
                                 <p className="text-sm">{msg.message}</p>
                                 <div className="flex items-center gap-2 mt-1 text-xs">
                                     <span className={isExpertMessage ? 'text-gray-800' : 'text-gray-400'}>
                                         {new Date(msg.date_sent).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                     </span>
                                     {isExpertMessage ? (
                                         <span className="flex items-center gap-1 text-gray-800">
                                             {isRead ? <CheckCheck className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                                             {isRead && readDate && (
                                                 <span className="text-[10px] text-gray-600">
                                                     {readDate.toLocaleString('pl-PL', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                                                 </span>
                                             )}
                                         </span>
                                     ) : (
                                         isRead && readDate && (
                                             <span className={isExpertMessage ? 'text-gray-800' : 'text-gray-400'}>
                                                 {readDate.toLocaleString('pl-PL', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                                             </span>
                                         )
                                     )}
                                 </div>
                             </div>
                        </div>
                    );
                })}
                <div ref={messagesEndRef} />
            </div>
            <div className="p-4 border-t border-gray-800 bg-mystic-bg">
                <form onSubmit={handleSubmit} className="flex items-center gap-3">
                    <textarea value={text} onChange={e => setText(e.target.value)} placeholder={`Reply as ${expert.name}...`}
                        className="flex-1 bg-mystic-inset border border-gray-700 rounded-lg p-2 text-sm text-white resize-none" rows={2}/>
                    <button type="submit" className="bg-mystic-accent text-black p-3 rounded-lg font-bold">Send</button>
                </form>
            </div>
        </div>
    );
};


// --- Main Component ---
const CmsChatPage: React.FC<{ currentUser: UserProfile }> = ({ currentUser }) => {
    const [users, setUsers] = useState<UserProfile[]>([]);
    const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [selectedExpert, setSelectedExpert] = useState<Expert | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [loading, setLoading] = useState<'users' | 'convos' | 'messages' | false>(false);

    useEffect(() => {
        const fetchUsers = async (withLoader: boolean) => {
            if (withLoader) setLoading('users');
            try {
                const res = await fetch('/api/chat?list_cms_users=true', { headers: { 'x-admin-phone': currentUser.phone }});
                if (res.ok) setUsers(await res.json());
            } catch (e) { console.error(e); }
            finally { if (withLoader) setLoading(false); }
        };

        fetchUsers(true);

        // polling user list dla aktualizacji badge'y, bez spinnera
        const interval = setInterval(() => fetchUsers(false), 20000);
        return () => clearInterval(interval);
    }, [currentUser]);

    useEffect(() => {
        if (!selectedUser) {
            setConversations([]);
            setSelectedExpert(null);
            return;
        };
        const fetchConversations = async () => {
            setLoading('convos');
            setSelectedExpert(null);
            setMessages([]);
            try {
                const res = await fetch(`/api/chat?for_user_id=${selectedUser.id}`, { headers: { 'x-admin-phone': currentUser.phone }});
                if (res.ok) setConversations(await res.json());
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        fetchConversations();
    }, [selectedUser, currentUser]);

    useEffect(() => {
        if (!selectedUser || !selectedExpert) {
            setMessages([]);
            return;
        }

        const fetchMessagesAndConvos = async (withLoader: boolean) => {
            if (withLoader) setLoading('messages');
            try {
                const res = await fetch(`/api/chat?with_user_id=${selectedExpert.user_id}&as_user_id=${selectedUser.id}`, { headers: { 'x-admin-phone': currentUser.phone }});
                if (res.ok) setMessages(await res.json());

                // po odczycie przez admina odśwież rozmowy (badge'y w kolumnie 2)
                const convRes = await fetch(`/api/chat?for_user_id=${selectedUser.id}`, { headers: { 'x-admin-phone': currentUser.phone }});
                if (convRes.ok) setConversations(await convRes.json());

                // oraz odśwież listę użytkowników (badge w kolumnie 1), żeby zliczanie było natychmiast aktualne
                if (withLoader) {
                    try {
                        const usersRes = await fetch('/api/chat?list_cms_users=true', { headers: { 'x-admin-phone': currentUser.phone } });
                        if (usersRes.ok) setUsers(await usersRes.json());
                    } catch (err) {
                        console.error(err);
                    }
                }
            } catch (e) { console.error(e); }
            finally { if (withLoader) setLoading(false); }
        };

        fetchMessagesAndConvos(true);

        // polling co 10s – bez spinnera
        const interval = setInterval(() => fetchMessagesAndConvos(false), 10000);
        return () => clearInterval(interval);
    }, [selectedExpert, selectedUser, currentUser]);

    const handleSendMessage = async (text: string) => {
        if (!selectedUser || !selectedExpert) return;

        const newMessage: ChatMessage = {
            id: Date.now(),
            sender_id: selectedExpert.user_id!,
            recipient_id: selectedUser.id!,
            message: text,
            date_sent: new Date().toISOString(),
            date_read: null,
            type: 'txt',
            media: null,
        };
        setMessages(prev => [...prev, newMessage]);

        try {
            const res = await fetch('/api/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                body: JSON.stringify({
                    sender_id: selectedExpert.user_id,
                    recipient_id: selectedUser.id,
                    message: text,
                }),
            });
            if (!res.ok) throw new Error("Failed to send");
            const data = await res.json();
            // Replace temp message with real one from DB
            setMessages(prev => prev.map(m => m.id === newMessage.id ? { ...m, id: data.id } : m));
        } catch (e) {
            console.error(e);
            setMessages(prev => prev.filter(m => m.id !== newMessage.id));
        }
    };

    return (
        <div className="h-[calc(100vh-56px)] flex">
            <div className="w-1/4 border-r border-gray-800">
                {loading === 'users' ? <Loader2 className="animate-spin m-auto"/> : <CmsUserList users={users} selectedUserId={selectedUser?.id || null} onSelect={setSelectedUser} />}
            </div>
            <div className="w-1/4 border-r border-gray-800">
                {!selectedUser ? <div className="p-8 text-center text-sm text-gray-500">Select a user to see conversations.</div> :
                 loading === 'convos' ? <Loader2 className="animate-spin m-auto"/> : <CmsExpertList conversations={conversations} selectedExpertId={selectedExpert?.rowid || null} onSelect={setSelectedExpert} />}
            </div>
            <div className="w-1/2">
                 {!selectedExpert ? <div className="p-8 text-center text-sm text-gray-500">Select a conversation to view chat.</div> :
                 loading === 'messages' ? <Loader2 className="animate-spin m-auto"/> : <CmsChatWindow user={selectedUser!} expert={selectedExpert} messages={messages} onSendMessage={handleSendMessage} />}
            </div>
        </div>
    );
};

export default CmsChatPage;